
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import hostlink from '../Hostlink/hostlink';
import * as XLSX from 'xlsx';
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    StyleSheet
} from '@react-pdf/renderer';

const Rpt_PurchaseReport_GR = () => {

    const usershopid = localStorage.getItem('MYPCKARTUSER')
        ? JSON.parse(localStorage.getItem('MYPCKARTUSER'))[0].id
        : '0';

    const today = new Date();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [frmdata, setfrmdata] = useState({
        fromdate: "2024-01-01",
        todate: today.toISOString().split('T')[0],
        deptcode: 0,
        status: 0
    });

    const { fromdate, todate } = frmdata;

    const tableRef = useRef(null);

    // ---------------------------------------
    // DATE FORMAT
    // ---------------------------------------
    const formatDate = (dateValue) => {
        if (!dateValue) return '-';

        const date = new Date(dateValue);

        if (isNaN(date.getTime())) return '-';

        return date
            .toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit'
            })
            .replace(/\//g, '/');
    };

    // ---------------------------------------
    // NUMBER FORMAT
    // ---------------------------------------
    const formatNumber = (value, decimals = 2) => {
        const number = Number(value);

        if (!Number.isFinite(number)) return '0.00';

        return number.toLocaleString('en-IN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // ---------------------------------------
    // INPUT CHANGE
    // ---------------------------------------
    const onChangevalue = (e) => {
        const { name, value } = e.target;

        if (name === 'fromdate' || name === 'todate') {
            setfrmdata({
                ...frmdata,
                [name]: value
            });
            return;
        }

        const sanitizedValue = value.replace(
            /['"!`@#$%^&*+{}|;:,.<>?=]/g,
            ''
        );

        setfrmdata({
            ...frmdata,
            [name]: sanitizedValue
        });
    };

    // ---------------------------------------
    // LOAD DATA
    // ---------------------------------------
    const loaddata = async () => {

        if (!fromdate || !todate) {
            return;
        }

        if (fromdate > todate) {
            alert('From Date cannot be greater than To Date.');
            return;
        }

        try {
            setLoading(true);

            const billResponse = await axios.get(
                `${hostlink}/PurchaseMasviewrpt/10/${usershopid}/${fromdate}/${todate}`
            );

            setData(
                Array.isArray(billResponse.data)
                    ? billResponse.data
                    : []
            );

        } catch (error) {
            console.log('Data Not Found...', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial load intentionally kept unchanged.
        // Data loads when user clicks Show.
    }, []);

    // ---------------------------------------
    // TOTALS
    // ---------------------------------------
    const totals = useMemo(() => {

        const totalMtr = data.reduce(
            (total, res) =>
                total + Number(res?.mtr || 0) * -1,
            0
        );

        const totalRoll = data.reduce(
            (total, res) =>
                total + Number(res?.roll || 0) * -1,
            0
        );

        const totalAmount = data.reduce(
            (total, res) =>
                total +
                (
                    Number(res?.mtr || 0) *
                    Number(res?.rate || 0) *
                    -1
                ),
            0
        );

        const uniqueBills = data.filter((res, index) => {
            return index === 0 ||
                res.billno !== data[index - 1]?.billno;
        }).length;

        return {
            totalMtr,
            totalRoll,
            totalAmount,
            uniqueBills,
            totalRecords: data.length
        };

    }, [data]);

    // ---------------------------------------
    // EXCEL
    // ---------------------------------------
    const exportToExcel = () => {

        if (!data.length) {
            alert('No data available to export.');
            return;
        }

        const ws = XLSX.utils.table_to_sheet(tableRef.current);

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            'GR Report'
        );

        XLSX.writeFile(
            wb,
            'GR Report.xlsx'
        );
    };

    // ---------------------------------------
    // SUMMARY CARD
    // ---------------------------------------
    const SummaryCard = ({
        icon,
        title,
        value,
        subtitle,
        iconClass = 'text-primary'
    }) => {

        return (
            <div className="col-6 col-md-4 col-xl">

                <div
                    className="card border-0 h-100"
                    style={{
                        borderRadius: '16px',
                        boxShadow: '0 5px 18px rgba(0,0,0,0.07)',
                        background: '#ffffff'
                    }}
                >

                    <div className="card-body p-3">

                        <div className="d-flex justify-content-between align-items-start">

                            <div>

                                <div
                                    className="text-muted mb-1"
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {title}
                                </div>

                                <div
                                    style={{
                                        fontSize: '21px',
                                        fontWeight: '700',
                                        color: '#212529'
                                    }}
                                >
                                    {value}
                                </div>

                                {subtitle && (
                                    <div
                                        className="text-muted mt-1"
                                        style={{
                                            fontSize: '11px'
                                        }}
                                    >
                                        {subtitle}
                                    </div>
                                )}

                            </div>

                            <div
                                className={iconClass}
                                style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#f4f7fb',
                                    fontSize: '18px'
                                }}
                            >
                                <i className={icon}></i>
                            </div>

                        </div>

                    </div>

                </div>

            </div>
        );
    };

    // ---------------------------------------
    // RETURN
    // ---------------------------------------
    return (

        <div
            className="container-fluid py-3"
            style={{
                background: '#f6f8fb',
                minHeight: '100vh'
            }}
        >

            {/* =========================================
                HEADER
            ========================================= */}

            <div
                className="card border-0 mb-3"
                style={{
                    borderRadius: '18px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                }}
            >

                <div
                    className="card-body p-3 p-md-4"
                    style={{
                        background:
                            'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)'
                    }}
                >

                    <div className="row align-items-center g-3">

                        <div className="col-lg-4">

                            <div className="d-flex align-items-center">

                                <div
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '14px',
                                        background:
                                            'linear-gradient(135deg, #0d6efd, #4dabf7)',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '12px',
                                        boxShadow:
                                            '0 5px 15px rgba(13,110,253,0.25)'
                                    }}
                                >
                                    <i className="fa-solid fa-arrow-rotate-left"></i>
                                </div>

                                <div>

                                    <h4
                                        className="mb-1"
                                        style={{
                                            fontWeight: '700',
                                            color: '#1f2937'
                                        }}
                                    >
                                        GR Report
                                    </h4>

                                    <div
                                        className="text-muted"
                                        style={{
                                            fontSize: '12px'
                                        }}
                                    >
                                        Goods Receipt report
                                    </div>

                                </div>

                            </div>

                        </div>

                        {/* DATE FILTERS */}

                        <div className="col-sm-6 col-lg-3">

                            <label
                                htmlFor="fromdate"
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                From Date
                            </label>

                            <div className="input-group">

                                <span className="input-group-text bg-white">
                                    <i className="fa-regular fa-calendar text-primary"></i>
                                </span>

                                <input
                                    type="date"
                                    id="fromdate"
                                    name="fromdate"
                                    value={fromdate}
                                    onChange={onChangevalue}
                                    className="form-control"
                                    style={{
                                        fontSize: '13px',
                                        boxShadow: 'none'
                                    }}
                                />

                            </div>

                        </div>

                        <div className="col-sm-6 col-lg-3">

                            <label
                                htmlFor="todate"
                                className="form-label mb-1"
                                style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#6c757d'
                                }}
                            >
                                To Date
                            </label>

                            <div className="input-group">

                                <span className="input-group-text bg-white">
                                    <i className="fa-regular fa-calendar-check text-primary"></i>
                                </span>

                                <input
                                    type="date"
                                    id="todate"
                                    name="todate"
                                    value={todate}
                                    min={fromdate}
                                    onChange={onChangevalue}
                                    className="form-control"
                                    style={{
                                        fontSize: '13px',
                                        boxShadow: 'none'
                                    }}
                                />

                            </div>

                        </div>

                        {/* BUTTONS */}

                        <div className="col-lg-2">

                            <label
                                className="form-label mb-1 d-none d-lg-block"
                                style={{
                                    fontSize: '12px',
                                    color: 'transparent'
                                }}
                            >
                                Actions
                            </label>

                            <div className="d-flex gap-2">

                                <button
                                    type="button"
                                    onClick={loaddata}
                                    className="btn btn-primary btn-sm flex-grow-1"
                                    disabled={loading}
                                    style={{
                                        borderRadius: '9px',
                                        fontWeight: '600',
                                        padding: '8px 12px'
                                    }}
                                >

                                    {loading ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                            ></span>
                                            Loading
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-magnifying-glass-chart me-1"></i>
                                            Show
                                        </>
                                    )}

                                </button>

                            </div>

                        </div>

                    </div>

                    {/* EXPORT BUTTONS */}

                    <div
                        className="d-flex justify-content-end gap-2 mt-3 pt-3"
                        style={{
                            borderTop: '1px solid #edf0f4'
                        }}
                    >

                        <button
                            type="button"
                            onClick={exportToExcel}
                            className="btn btn-sm btn-outline-success"
                            style={{
                                borderRadius: '8px',
                                fontWeight: '600'
                            }}
                        >
                            <i className="fa-solid fa-file-excel me-1"></i>
                            Excel
                        </button>

                        <PDFDownloadLink
                            document={
                                <PDFDocument
                                    data={data}
                                    fromdate={fromdate}
                                    todate={todate}
                                />
                            }
                            fileName="GR Report.pdf"
                            style={{
                                textDecoration: 'none'
                            }}
                        >
                            {({ loading }) => (

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={loading}
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: '600'
                                    }}
                                >
                                    <i className="fa-solid fa-file-pdf me-1"></i>
                                    {loading ? 'Preparing...' : 'PDF'}
                                </button>

                            )}
                        </PDFDownloadLink>

                    </div>

                </div>

            </div>


            {/* =========================================
                SUMMARY CARDS
            ========================================= */}

            <div className="row g-3 mb-3">

                <SummaryCard
                    icon="fa-solid fa-receipt"
                    title="Total Bills"
                    value={totals.uniqueBills}
                    subtitle="Unique bill numbers"
                    iconClass="text-primary"
                />

                <SummaryCard
                    icon="fa-solid fa-list"
                    title="Total Records"
                    value={totals.totalRecords}
                    subtitle="Report entries"
                    iconClass="text-info"
                />

                <SummaryCard
                    icon="fa-solid fa-ruler-horizontal"
                    title="Total Mtr"
                    value={formatNumber(totals.totalMtr)}
                    subtitle="Total meter"
                    iconClass="text-success"
                />

                <SummaryCard
                    icon="fa-solid fa-scroll"
                    title="Total Roll"
                    value={formatNumber(totals.totalRoll, 0)}
                    subtitle="Total rolls"
                    iconClass="text-warning"
                />

                <SummaryCard
                    icon="fa-solid fa-indian-rupee-sign"
                    title="Bill Amount"
                    value={`₹ ${formatNumber(totals.totalAmount)}`}
                    subtitle="Mtr × Rate"
                    iconClass="text-danger"
                />

            </div>


            {/* =========================================
                TABLE CARD
            ========================================= */}

            <div
                className="card border-0"
                style={{
                    borderRadius: '18px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.06)',
                    overflow: 'hidden'
                }}
            >

                {/* TABLE HEADER */}

                <div className="card-header bg-white border-0 p-3">

                    <div className="d-flex justify-content-between align-items-center">

                        <div>

                            <h6
                                className="mb-1"
                                style={{
                                    fontWeight: '700',
                                    color: '#212529'
                                }}
                            >
                                <i className="fa-solid fa-table me-2 text-primary"></i>
                                GR Details
                            </h6>

                            <small className="text-muted">
                                {fromdate} to {todate}
                            </small>

                        </div>

                        <span
                            className="badge rounded-pill bg-primary-subtle text-primary"
                            style={{
                                padding: '8px 12px'
                            }}
                        >
                            {data.length} Records
                        </span>

                    </div>

                </div>


                {/* TABLE */}

                <div
                    style={{
                        maxHeight: 'calc(100vh - 360px)',
                        minHeight: '250px',
                        overflow: 'auto'
                    }}
                >

                    <table
                        className="table table-hover mb-0"
                        ref={tableRef}
                        style={{
                            minWidth: '1000px'
                        }}
                    >

                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                background: '#f4f7fb'
                            }}
                        >

                            <tr>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    GR Date
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Bill No
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Bill Date
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Party
                                </th>

                                <th
                                    className="text-start"
                                    style={thStyle}
                                >
                                    Sort No
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Mtr
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Rate
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Roll
                                </th>

                                <th
                                    className="text-end"
                                    style={thStyle}
                                >
                                    Bill Amount
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center py-5"
                                    >

                                        <div
                                            className="spinner-border text-primary mb-2"
                                            role="status"
                                        ></div>

                                        <div className="text-muted">
                                            Loading GR report...
                                        </div>

                                    </td>

                                </tr>

                            ) : data.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="9"
                                        className="text-center py-5"
                                    >

                                        <div
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '50%',
                                                background: '#f1f5f9',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 12px',
                                                fontSize: '24px',
                                                color: '#94a3b8'
                                            }}
                                        >
                                            <i className="fa-solid fa-inbox"></i>
                                        </div>

                                        <div
                                            style={{
                                                fontWeight: '600',
                                                color: '#475569'
                                            }}
                                        >
                                            No GR records found
                                        </div>

                                        <small className="text-muted">
                                            Select a date range and click Show
                                        </small>

                                    </td>

                                </tr>

                            ) : (

                                data.map((res, x) => {

                                    const isDuplicate =
                                        x > 0 &&
                                        res.billno === data[x - 1]?.billno;

                                    return (

                                        <tr key={x}>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                {isDuplicate
                                                    ? ''
                                                    : formatDate(res.billdate)}
                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >

                                                {isDuplicate ? '' : (

                                                    <span
                                                        style={{
                                                            fontWeight: '600',
                                                            color: '#0d6efd'
                                                        }}
                                                    >
                                                        {res.billno}
                                                    </span>

                                                )}

                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                {isDuplicate
                                                    ? ''
                                                    : formatDate(res.grdate)}
                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                {isDuplicate
                                                    ? ''
                                                    : res.accname}
                                            </td>

                                            <td
                                                className="text-start"
                                                style={tdStyle}
                                            >
                                                <span
                                                    className="badge bg-light text-dark"
                                                    style={{
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {res.sortno}
                                                </span>
                                            </td>

                                            <td
                                                className="text-end"
                                                style={tdStyle}
                                            >
                                                {formatNumber(
                                                    Number(res.mtr || 0) * -1
                                                )}
                                            </td>

                                            <td
                                                className="text-end"
                                                style={tdStyle}
                                            >
                                                ₹ {formatNumber(res.rate)}
                                            </td>

                                            <td
                                                className="text-end"
                                                style={tdStyle}
                                            >
                                                {formatNumber(
                                                    Number(res.roll || 0) * -1,
                                                    0
                                                )}
                                            </td>

                                            <td
                                                className="text-end"
                                                style={{
                                                    ...tdStyle,
                                                    fontWeight:
                                                        isDuplicate
                                                            ? '400'
                                                            : '600'
                                                }}
                                            >
                                                {isDuplicate
                                                    ? ''
                                                    : `₹ ${formatNumber(
                                                        Number(
                                                            res.billamt || 0
                                                        ) * -1
                                                    )}`}
                                            </td>

                                        </tr>

                                    );

                                })

                            )}

                        </tbody>


                        {/* TOTAL */}

                        {data.length > 0 && (

                            <tfoot
                                style={{
                                    position: 'sticky',
                                    bottom: 0,
                                    zIndex: 4
                                }}
                            >

                                <tr
                                    style={{
                                        background: '#eaf2ff'
                                    }}
                                >

                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>

                                    <td
                                        className="text-end"
                                        style={{
                                            fontWeight: '700',
                                            padding: '13px'
                                        }}
                                    >
                                        Total
                                    </td>

                                    <td
                                        className="text-end"
                                        style={{
                                            fontWeight: '700',
                                            padding: '13px'
                                        }}
                                    >
                                        {formatNumber(totals.totalMtr)}
                                    </td>

                                    <td></td>

                                    <td
                                        className="text-end"
                                        style={{
                                            fontWeight: '700',
                                            padding: '13px'
                                        }}
                                    >
                                        {formatNumber(totals.totalRoll, 0)}
                                    </td>

                                    <td
                                        className="text-end"
                                        style={{
                                            fontWeight: '700',
                                            padding: '13px',
                                            color: '#0d6efd'
                                        }}
                                    >
                                        ₹ {formatNumber(totals.totalAmount)}
                                    </td>

                                </tr>

                            </tfoot>

                        )}

                    </table>

                </div>

            </div>

        </div>
    );
};


// =====================================================
// TABLE STYLES
// =====================================================

const thStyle = {
    padding: '13px 12px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #e2e8f0'
};

const tdStyle = {
    padding: '11px 12px',
    fontSize: '12px',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #f1f5f9'
};


// =====================================================
// PDF DOCUMENT
// =====================================================

const PDFDocument = ({
    data,
    fromdate,
    todate
}) => {

    const totalMtr = data.reduce(
        (total, res) =>
            total + Number(res?.mtr || 0) * -1,
        0
    );

    const totalRoll = data.reduce(
        (total, res) =>
            total + Number(res?.roll || 0) * -1,
        0
    );

    const totalAmount = data.reduce(
        (total, res) =>
            total +
            (
                Number(res?.mtr || 0) *
                Number(res?.rate || 0) *
                -1
            ),
        0
    );

    const pdfFormatDate = (dateValue) => {

        if (!dateValue) return '-';

        const date = new Date(dateValue);

        if (isNaN(date.getTime())) return '-';

        return date
            .toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit'
            });
    };

    const pdfNumber = (value) => {

        const number = Number(value);

        if (!Number.isFinite(number)) {
            return '0.00';
        }

        return number.toFixed(2);
    };

    return (

        <Document>

            <Page
                orientation="landscape"
                size="A4"
                style={pdfStyles.page}
            >

                <View style={pdfStyles.container}>

                    {/* PDF HEADER */}

                    <Text style={pdfStyles.title}>
                        GR REPORT
                    </Text>

                    <Text style={pdfStyles.subtitle}>
                        From : {fromdate}    To : {todate}
                    </Text>


                    {/* TABLE */}

                    <View style={pdfStyles.table}>

                        <View
                            style={[
                                pdfStyles.tableRow,
                                pdfStyles.headerRow
                            ]}
                        >

                            <Text style={pdfStyles.headerCell}>
                                GR Date
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Bill No
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Bill Date
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Party
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Sort No
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Mtr
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Rate
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Roll
                            </Text>

                            <Text style={pdfStyles.headerCell}>
                                Bill Amount
                            </Text>

                        </View>


                        {data.map((res, x) => {

                            const isDuplicate =
                                x > 0 &&
                                res.billno === data[x - 1]?.billno;

                            return (

                                <View
                                    key={x}
                                    style={pdfStyles.tableRow}
                                >

                                    <Text style={pdfStyles.cell}>
                                        {isDuplicate
                                            ? ''
                                            : pdfFormatDate(res.billdate)}
                                    </Text>

                                    <Text style={pdfStyles.cell}>
                                        {isDuplicate
                                            ? ''
                                            : String(res.billno ?? '')}
                                    </Text>

                                    <Text style={pdfStyles.cell}>
                                        {isDuplicate
                                            ? ''
                                            : pdfFormatDate(res.grdate)}
                                    </Text>

                                    <Text style={pdfStyles.partyCell}>
                                        {isDuplicate
                                            ? ''
                                            : String(res.accname ?? '')}
                                    </Text>

                                    <Text style={pdfStyles.cell}>
                                        {String(res.sortno ?? '')}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(
                                            Number(res.mtr || 0) * -1
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(res.rate)}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {pdfNumber(
                                            Number(res.roll || 0) * -1
                                        )}
                                    </Text>

                                    <Text style={pdfStyles.numberCell}>
                                        {isDuplicate
                                            ? ''
                                            : pdfNumber(
                                                Number(
                                                    res.billamt || 0
                                                ) * -1
                                            )}
                                    </Text>

                                </View>

                            );

                        })}


                        {/* TOTAL */}

                        <View
                            style={[
                                pdfStyles.tableRow,
                                pdfStyles.totalRow
                            ]}
                        >

                            <Text style={pdfStyles.cell}></Text>
                            <Text style={pdfStyles.cell}></Text>
                            <Text style={pdfStyles.cell}></Text>
                            <Text style={pdfStyles.cell}></Text>

                            <Text style={pdfStyles.totalCell}>
                                Total :
                            </Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalMtr)}
                            </Text>

                            <Text style={pdfStyles.cell}></Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalRoll)}
                            </Text>

                            <Text style={pdfStyles.totalNumberCell}>
                                {pdfNumber(totalAmount)}
                            </Text>

                        </View>

                    </View>

                </View>

            </Page>

        </Document>
    );
};


// =====================================================
// PDF STYLES
// =====================================================

const pdfStyles = StyleSheet.create({

    page: {
        padding: 20,
        backgroundColor: '#ffffff'
    },

    container: {
        width: '100%'
    },

    title: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 6
    },

    subtitle: {
        fontSize: 9,
        textAlign: 'center',
        marginBottom: 15
    },

    table: {
        display: 'table',
        width: '100%',
        borderWidth: 1,
        borderColor: '#d1d5db'
    },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#e5e7eb',
        minHeight: 22,
        alignItems: 'center'
    },

    headerRow: {
        backgroundColor: '#eaf2ff'
    },

    totalRow: {
        backgroundColor: '#eaf2ff',
        borderBottomWidth: 0
    },

    headerCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 5,
        flex: 1,
        textAlign: 'center'
    },

    cell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'center'
    },

    partyCell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'left'
    },

    numberCell: {
        fontSize: 7,
        padding: 4,
        flex: 1,
        textAlign: 'right'
    },

    totalCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'right'
    },

    totalNumberCell: {
        fontSize: 7,
        fontWeight: 'bold',
        padding: 4,
        flex: 1,
        textAlign: 'right'
    }
});


export default Rpt_PurchaseReport_GR;

